import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { AllowanceProposalData } from '~/data/proposals';
import {
  CommonEmployeeProposalFormData,
  mapDefaultDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { convertToDateOrNull } from '~/utils/helpers/date';

export type AllowanceProposalFormData = CommonEmployeeProposalFormData & {
  allowanceType: string;
  allowanceFrequency: string;
  startDate: Date | null;
  endDate?: Date | null;
  allowanceAmount: number;
  taxableAmount: number;
  allowanceInfo: string;
};

export const mapDefaultData = (
  targetEmploymentId: number,
  proposalData?: AllowanceProposalData,
  defaultResponsibleHrId?: number,
): AllowanceProposalFormData => {
  const { defaultToEmptyString, defaultResponsibleHR, defaultToNumber } =
    mapDefaultDataUtils();

  return {
    employmentId: defaultToEmptyString(
      proposalData?.employmentFK || targetEmploymentId,
    ),
    allowanceType: defaultToEmptyString(proposalData?.allowanceTypeFK),
    allowanceFrequency: defaultToEmptyString(proposalData?.frequencyFK),
    startDate: convertToDateOrNull(proposalData?.startDate),
    endDate: convertToDateOrNull(proposalData?.endDate),
    allowanceAmount: defaultToNumber(proposalData?.amount, 0),
    taxableAmount: defaultToNumber(proposalData?.taxableAmount, 0),
    allowanceInfo: defaultToEmptyString(proposalData?.info),
    responsibleHR: defaultResponsibleHR(
      defaultResponsibleHrId,
      defaultResponsibleHrId,
    ),
  };
};

export const useValidationSchema = (isEmploymentIdRequired: boolean) => {
  const { t } = useTranslation();

  return yup.object().shape<AllowanceProposalFormData>({
    employmentId: isEmploymentIdRequired
      ? yup.string().required(t('fieldIsRequired'))
      : yup.string().default(''),
    allowanceType: yup.string().required(t('fieldIsRequired')),
    allowanceFrequency: yup.string().required(t('fieldIsRequired')),
    startDate: yup.date().nullable().required(t('fieldIsRequired')),
    endDate: yup.date().nullable(),
    allowanceAmount: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(t('fieldIsRequired')),
    taxableAmount: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(t('fieldIsRequired')),
    allowanceInfo: yup.string(),
    responsibleHR: yup.number().required(t('fieldIsRequired')),
  });
};
